
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../services/authService';
import { UserPlus, Mail, Lock, User as UserIcon, Loader2 } from 'lucide-react';
import Logo from '../components/Logo';

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const result = await authService.signup(email, password, name);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Signup failed.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center mb-6">
            <Logo size={80} />
          </div>
          <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tight">Join the Directory</h2>
          <p className="mt-2 text-sm text-gray-500 font-medium italic">List your practice on Australia's premium network.</p>
        </div>

        <div className="bg-white p-10 rounded-3xl shadow-2xl border border-gray-100">
          <form className="space-y-6" onSubmit={handleSignup}>
            {error && (
              <div className="bg-red-50 text-red-700 p-4 rounded-xl text-xs font-bold border border-red-100 flex items-center gap-2">
                 <span className="w-2 h-2 bg-red-500 rounded-full"></span> {error}
              </div>
            )}
            
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Full Name / Practice Principal</label>
              <div className="relative">
                <UserIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium"
                  placeholder="e.g. Sarah Jenkins"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Practice Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium"
                  placeholder="office@practice.com.au"
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Create Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-xl outline-none focus:ring-2 focus:ring-brand-500 transition shadow-inner font-medium"
                  placeholder="Minimum 8 characters"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-4 px-4 bg-brand-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-black transition shadow-xl active:scale-[0.98] disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <UserPlus size={18} />}
              {loading ? 'Creating Account...' : 'Register as Professional'}
            </button>
          </form>

          <div className="mt-8 text-center pt-8 border-t border-gray-50">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Already have an account?</p>
            <Link to="/login" className="text-brand-600 font-black text-xs uppercase tracking-widest hover:underline">
              Sign In to your Dashboard
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
